import { ModeOfTransport } from "@iventis/domain-model/model/modeOfTransport";
import { coordinatesToString, mapboxKey } from "@iventis/utilities";
import GeoJSON from "geojson";
import { RouteWaypoint } from "@iventis/map-types";
import { RouteFinderResponse } from "../../types/internal";

export type MapBoxRouteResponse = { distance: number; routes: { geometry: GeoJSON.LineString; duration: number }[]; waypoints: { location: GeoJSON.Position; name: string }[] };

/**
 * Generates a route using the Mapbox Directions API.
 * @param routeWaypoints an array of route waypoints for the route.
 * @param modeOfTransport the mode of transport to use for the route.
 * @returns A linestring GeoJSON object representing the route.
 */
export function generateRoute(routeWaypoints: RouteWaypoint[], modeOfTransport: ModeOfTransport) {
    return new Promise<RouteFinderResponse>((resolve, reject) => {
        if (routeWaypoints.length > 25) {
            throw new Error("Mapbox only supports 25 waypoints per route.");
        }
        if (routeWaypoints.length < 2) {
            throw new Error("At least 2 waypoints are required to generate a route.");
        }
        const waypoints = routeWaypoints.map((waypoint) => [waypoint.coordinates.join(",")]);
        const mode = modeOfTransportToMapboxMode[modeOfTransport];
        const url = `https://api.mapbox.com/directions/v5/mapbox/${mode}/${waypoints.join(";")}?geometries=geojson&access_token=${mapboxKey}&overview=full`;
        fetch(url)
            .then((response) => response.json())
            .then((data: MapBoxRouteResponse) => {
                const route = data.routes[0];
                const geojson = route.geometry;

                resolve({
                    route: geojson,
                    duration: route.duration,
                    waypoints: routeWaypoints.map<RouteWaypoint>((waypoint, index) => ({
                        ...waypoint,
                        // If the returned waypoint has a name, use that, otherwise use the coordinates
                        name: getWaypointName(data, waypoint, index),
                        order: index + 1,
                    })),
                });
            })
            .catch((error) => {
                reject(error);
            });
    });
}

/**
 * Gets the waypoint name based on the response and the existing waypoint.
 *  If the waypoint already has a name that isn't a coordinate, it will return that name
 *  If the waypoint already has a name that is a coordinate, it will return the name from the response
 *  If the waypoint doesn't have a name, but the response does, it will return the name from the response
 *  If the waypoint doesn't have a name, and neither does the response, it will return a coordinate string
 * */
export const getWaypointName = (mapboxData: MapBoxRouteResponse, waypoint: RouteWaypoint, index: number) =>
    waypoint.name == null || isWaypointNameCoordinatePlaceholder(waypoint.name)
        ? mapboxData.waypoints[index].name?.length > 0
            ? mapboxData.waypoints[index].name
            : coordinatesToString(waypoint.coordinates)
        : waypoint.name;

const isWaypointNameCoordinatePlaceholder = (name: string) => {
    const includesComma = name?.includes(",");
    if (!includesComma || name.split(",").length !== 2) {
        return name;
    }
    const firstBit = name.split(",")[0];
    const secondBit = name.split(",")[1];
    const firstBitIsNumber = !Number.isNaN(Number(firstBit));
    const secondBitIsNumber = !Number.isNaN(Number(secondBit));
    return firstBitIsNumber && secondBitIsNumber;
};

/**
 * Converts a mode of transport from the iventis domain model to a mode of transport that mapbox supports
 * @param modeOfTransport the mode of transport to convert
 * @returns the mapbox compatible mode of transport
 */
const modeOfTransportToMapboxMode: Record<ModeOfTransport, string> = {
    [ModeOfTransport.Car]: "driving",
    [ModeOfTransport.Cycle]: "cycling",
    [ModeOfTransport.Walk]: "walking",
};
